import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { Alert, Box, Chip, CircularProgress, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatCpf } from "@safrater/ui";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";
import { MembershipCard, type AccessResponse } from "./AccessDrawer";

/**
 * Tela de detalhe do usuário com abas. A aba "Instituições" mostra os vínculos
 * e papéis por instituição (dados já escopados pelo backend: master vê todas,
 * admin de instituição vê só as suas). A edição de papéis por card é liberada
 * para master OU admin daquela instituição.
 */
export function UserDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState(0);

  const { data, isFetching, error } = useQuery({
    queryKey: ["user-access", id],
    queryFn: async () => (await api.get<AccessResponse>(`/users/${id}/access`)).data,
    enabled: !!id,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["user-access", id] });

  const canEdit = (institutionId: string) =>
    !!user?.isMasterAdmin || (user?.adminInstitutionIds ?? []).includes(institutionId);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton onClick={() => navigate("/users")} aria-label="Voltar"><ArrowBackIcon /></IconButton>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>{data?.name ?? "Usuário"}</Typography>
        {data?.isMasterAdmin && <Chip size="small" color="secondary" label="master admin" />}
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab label="Dados" />
        <Tab label="Instituições" />
      </Tabs>

      {isFetching && <CircularProgress size={24} />}
      {error && <Alert severity="error">Falha ao carregar dados do usuário.</Alert>}

      {data && tab === 0 && (
        <Stack spacing={1} sx={{ maxWidth: 480 }}>
          <Row label="Nome" value={data.name} />
          <Row label="E-mail" value={data.email} />
          <Row label="CPF" value={data.cpf ? formatCpf(data.cpf) : "—"} />
          <Row label="Status" value={data.status} />
          <Row label="Master admin" value={data.isMasterAdmin ? "Sim" : "Não"} />
        </Stack>
      )}

      {data && tab === 1 && (
        <Stack spacing={2}>
          {data.memberships.length === 0 && <Alert severity="info">Nenhuma instituição vinculada (visível para você).</Alert>}
          {data.memberships.map((m) => (
            <MembershipCard key={m.membershipId} m={m} onChanged={invalidate} editable={canEdit(m.institution.id)} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 120 }}>{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}
